import { ChakraProvider, extendTheme, ToastProviderProps } from '@chakra-ui/react';
import { useContext, useMemo, useEffect } from 'react';
import chroma from 'chroma-js';
import { MarketplaceContext } from 'src/contexts/marketplace';
import {
  DEFAULT_PRIMARY_COLOR_SCHEME,
  DEFAULT_SECONDARY_COLOR_SCHEME,
  WHITE,
  BLACK,
} from 'src/constants/ui';
import { getTextColor, getColorSwatch } from 'src/utils/color';
import { MarketplaceInfo, SimpleMarketplaceTheme } from 'src/api/v1-api';
import { preloadGoogleFonts, getFontFamily } from 'src/utils/dynamicFonts';

const ThemeProvider = ({
  children,
  marketplaceProvider = null,
}: {
  children: React.ReactNode;
  marketplaceProvider?: MarketplaceInfo | SimpleMarketplaceTheme | null;
}): JSX.Element => {
  let marketplace: MarketplaceInfo | SimpleMarketplaceTheme | null = marketplaceProvider;
  const marketplaceContext = useContext(MarketplaceContext);
  const { marketplace: marketplaceInfo } = marketplaceContext;

  if (!marketplace) {
    marketplace = marketplaceInfo;
  }

  const mainBG = chroma.valid(marketplace?.theme?.background_color)
    ? marketplace?.theme?.background_color
    : WHITE;
  const mainText = getTextColor(mainBG);
  const navBG = chroma.valid(marketplace?.theme?.nav_background_color)
    ? marketplace?.theme?.nav_background_color
    : mainBG;
  const navText = getTextColor(navBG);
  const borderRadius1 = marketplace?.theme?.border_radius_1 ?? '6px';
  const borderRadius2 = marketplace?.theme?.border_radius_2 ?? '4px';

  // Load fonts dynamically when marketplace changes
  useEffect(() => {
    if (typeof window !== 'undefined' && marketplace?.theme) {
      preloadGoogleFonts(marketplace.theme.font_1, marketplace.theme.font_2);
    }
  }, [marketplace?.theme?.font_1, marketplace?.theme?.font_2]);

  // Compute font families outside useMemo to ensure proper dependency tracking
  const font1Family = getFontFamily(marketplace?.theme?.font_1);
  const font2Family = getFontFamily(marketplace?.theme?.font_2);

  const theme = useMemo(() => {
    const overrides = {
      name: marketplace?.theme?.name,
      styles: {
        global: {
          body: {
            color: mainText,
            bgColor: mainBG,
            fontFamily: font2Family,
          },
        },
      },
      fonts: {
        heading: font1Family,
        body: font2Family,
        font_1: font1Family,
        font_2: font2Family,
      },
      border_radius: {
        border_radius_1: `${borderRadius1}`,
        border_radius_2: `${borderRadius2}`,
      },
      colors: {
        text: getColorSwatch(mainText),
        mainBG,
        formBG: WHITE, // Forms have white backgrounds in marketplace
        mainText,
        secondaryText: '#4A5568', // Slightly muted text for secondary content
        navBG,
        navText,
        primary: chroma.valid(marketplace?.theme?.primary_color)
          ? getColorSwatch(marketplace?.theme?.primary_color)
          : DEFAULT_PRIMARY_COLOR_SCHEME,
        secondary: chroma.valid(marketplace?.theme?.secondary_color)
          ? getColorSwatch(marketplace?.theme?.secondary_color)
          : DEFAULT_SECONDARY_COLOR_SCHEME,
        // Loma Branding Colors
        lomaGreen: {
          50: '#eafff5',
          100: '#ccffe5',
          200: '#9dfdd2',
          300: '#5ef7bb',
          400: '#17e098', // main color
          500: '#00cf8a',
          600: '#00a971',
          700: '#00875f',
          800: '#006b4c',
          900: '#005840',
          950: '#003225',
        },
        // technically more of a blue heavy green so use below 950 with caution
        lomaDarkGreen: {
          50: '#f1fcfa',
          100: '#d1f6f0',
          200: '#a4ebe2',
          300: '#6edacf',
          400: '#40c1b8',
          500: '#27a59f',
          600: '#1c8581',
          700: '#1b6a68',
          800: '#1a5554',
          900: '#1a4747',
          950: '#082526', // main color
        },
        lomaBlueViolet: {
          50: '#eff2ff',
          100: '#dbe2fe',
          200: '#becdff',
          300: '#92adfe',
          400: '#597efc', // main color
          500: '#3856f9',
          600: '#2235ee',
          700: '#1a22db',
          800: '#1c1eb1',
          900: '#1d218b',
          950: '#161755',
        },
      },
      components: {
        Alert: {
          baseStyle: {
            container: {
              color: BLACK,
              fontFamily: font2Family,
              borderRadius: `${borderRadius2}`,
            },
          },
        },
        Heading: {
          baseStyle: {
            fontFamily: font1Family,
          },
        },
        Input: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: `${borderRadius1}`,
                fontFamily: font2Family,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        NumberInput: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: `${borderRadius1}`,
                fontFamily: font2Family,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        Textarea: {
          variants: {
            outline: {
              bg: 'white',
              color: BLACK,
              borderRadius: `${borderRadius1}`,
              fontFamily: font2Family,
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        FormLabel: {
          baseStyle: {
            fontFamily: font2Family,
          },
        },
        Menu: {
          baseStyle: {
            item: {
              color: BLACK,
              fontFamily: font2Family,
            },
          },
        },
        ButtonGroup: {
          baseStyle: {
            Button: {
              color: BLACK,
              borderRadius: `${borderRadius1}`,
              fontFamily: font2Family,
            },
          },
        },
        Button: {
          baseStyle: {
            color: BLACK,
            borderRadius: `${borderRadius1}`,
            fontFamily: font2Family,
          },
        },
        Select: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: `${borderRadius1}`,
                fontFamily: font2Family,
              },
              icon: {
                color: BLACK,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        Drawer: {
          baseStyle: {
            dialog: {
              color: BLACK,
              fontFamily: font2Family,
            },
          },
        },
        Tag: {
          baseStyle: {
            container: {
              fontFamily: font2Family,
            },
            label: {
              textTransform: 'uppercase',
            },
          },
        },
        Popover: {
          baseStyle: {
            content: {
              color: BLACK,
              borderRadius: `${borderRadius2}`,
              fontFamily: font2Family,
            },
          },
        },
        Switch: {
          baseStyle: {
            track: {
              _checked: {
                bg: `${marketplace?.theme?.primary_color || DEFAULT_PRIMARY_COLOR_SCHEME[400]}`,
              },
            },
          },
        },
        Breadcrumb: {
          baseStyle: {
            item: {
              fontFamily: font2Family,
            },
          },
        },
        Modal: {
          baseStyle: {
            dialog: {
              borderRadius: `${borderRadius2}`,
              bg: WHITE,
              color: BLACK,
              fontFamily: font2Family,
            },
          },
        },
        Card: {
          baseStyle: {
            container: {
              borderRadius: `${borderRadius2}`,
            },
          },
        },
      },
    };
    return extendTheme(overrides);
    // only recompute when theme-relevant marketplace values change
  }, [
    marketplace?.theme?.name,
    marketplace?.theme?.primary_color,
    marketplace?.theme?.secondary_color,
    borderRadius1,
    borderRadius2,
    mainBG,
    mainText,
    navBG,
    navText,
    font1Family,
    font2Family,
  ]);

  const toastOptions = useMemo(() => {
    return {
      defaultOptions: {
        containerStyle: {
          borderRadius: `${borderRadius2}`,
          fontFamily: font2Family,
        },
      },
    } as ToastProviderProps;
  }, [borderRadius2, font2Family]);

  return (
    <ChakraProvider theme={theme} toastOptions={toastOptions}>
      {children}
    </ChakraProvider>
  );
};

export default ThemeProvider;
